// iter591 — изнесен от Dashboard.jsx (Command Center hero: статус + Twin Score)
// iter631 — заменено "Здравен индекс" с "Twin Score" (16-twin composite)
// iter632 — линкът сега скролва към Twin Card на същата страница вместо HI
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";

export default function HiScoreHero({ user, hiSummary, isDark, bg }) {
  const { API } = useAuth();
  const name = (user?.name || user?.email || "").split(" ")[0] || (bg ? "приятел" : "friend");

  // iter637/iter675 — Fetch Twin Score (18 близнака composite — incl. dental + BHI)
  const [twin, setTwin] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/health-twin/twin-score?days=30`, { timeout: 12000 })
      .then(({ data }) => setTwin(data)).catch(() => {});
    load();
    const i = setInterval(load, 5 * 60 * 1000);
    const evts = ["meal-logged", "meal-added", "meal-log-changed", "thyroid-lab-changed",
                  "stress-log-changed", "cycle-changed"];
    evts.forEach(e => window.addEventListener(e, load));
    return () => { clearInterval(i); evts.forEach(e => window.removeEventListener(e, load)); };
  }, [API]);

  const score100 = twin?.composite ?? null;
  const delta = hiSummary?.hi_score?.weekly_delta ?? null;
  // iter1045 — броят идва от бекенда (TWIN_UNIVERSE), а не от закован номер
  const twinCount = twin?.twins_with_data
    ?? Object.values(twin?.sub_scores || {}).filter(v => v != null).length;
  const totalTwins = twin?.twins_total ?? Object.keys(twin?.sub_scores || {}).length;
  const confidence = twin?.coverage_pct ?? 0;
  // iter583 — Health Status — soft, partner-tone (no scary wording)
  const status = score100 == null ? null
    : score100 >= 80 ? { label: bg ? "Отлично" : "Excellent", tone: "emerald" }
    : score100 >= 60 ? { label: bg ? "Добро" : "Good", tone: "sky" }
    : score100 >= 40 ? { label: bg ? "Има какво да подобрим заедно" : "Room to grow together", tone: "amber" }
    : { label: bg ? "Тепърва започваме заедно" : "Just getting started together", tone: "rose" };
  const statusTone = {
    emerald: isDark ? "text-emerald-300" : "text-emerald-700",
    sky:     isDark ? "text-sky-300"     : "text-sky-700",
    amber:   isDark ? "text-amber-300"   : "text-amber-700",
    rose:    isDark ? "text-rose-300"    : "text-rose-700",
  };

  const scrollToTwin = (e) => {
    e.preventDefault();
    const el = document.getElementById("unified-health-twin");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <a
      href="#unified-health-twin"
      onClick={scrollToTwin}
      data-testid="cc-hi-hero"
      className={`group block rounded-3xl p-6 sm:p-8 border transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer ${
        isDark
          ? "bg-gradient-to-br from-violet-900/30 via-[hsl(222,47%,9%)] to-indigo-900/20 border-violet-700/30 hover:border-violet-500/50"
          : "bg-gradient-to-br from-violet-50 via-white to-indigo-50 border-violet-100 hover:border-violet-300"
      }`}
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? `Здравейте, ${name}` : `Hi, ${name}`}
            </h1>
          </div>
          <div className={`mt-3 text-[11px] uppercase tracking-[0.14em] font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            Digital Health Twin
          </div>

          {/* iter583 — Health Status (semantic) — partner tone */}
          {status && (
            <div className="mt-1.5 flex items-baseline gap-2" data-testid="cc-hi-status">
              <span className={`text-2xl sm:text-3xl font-extrabold ${statusTone[status.tone]}`}>{status.label}</span>
            </div>
          )}

          {/* iter631 — Twin Score coverage (instead of "profile clarity") */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" data-testid="cc-hi-confidence-row">
            <span className={`font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {bg
                ? `Активни близнаци: `
                : `Active twins: `}
              <strong className="tabular-nums" data-testid="cc-hi-twin-count">{twinCount}/{totalTwins}</strong>
              {" · "}
              <strong className="tabular-nums">{confidence}%</strong>
              {bg ? " покритие" : " coverage"}
            </span>
          </div>

          {/* iter577 — Hero вече е портал → CTA визуален индикатор */}
          <div className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${isDark ? "text-violet-300" : "text-violet-700"} group-hover:translate-x-0.5 transition-transform`}>
            {bg ? "Виж пълния Twin профил" : "View full Twin profile"} →
          </div>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <div className={`text-[10px] uppercase tracking-[0.14em] font-bold mb-0.5 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            🧬 Twin Score
          </div>
          <div className="flex items-baseline gap-1">
            <span
              className={`text-5xl sm:text-6xl font-extrabold tabular-nums leading-none ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
              data-testid="cc-hi-score"
            >
              {score100 ?? "—"}
            </span>
            <span className={`text-2xl font-bold ${isDark ? "text-gray-500" : "text-gray-400"}`}>/ 100</span>
          </div>
          {delta != null && (
            <div
              className={`mt-1 text-xs font-semibold flex items-center gap-1 ${
                delta > 0 ? (isDark ? "text-emerald-300" : "text-emerald-700") :
                delta < 0 ? (isDark ? "text-rose-300" : "text-rose-700") :
                (isDark ? "text-gray-400" : "text-gray-500")
              }`}
              data-testid="cc-hi-delta"
            >
              {delta > 0 ? "↑" : delta < 0 ? "↓" : "·"} {delta > 0 ? "+" : ""}{delta} {bg ? "тази седмица" : "this week"}
            </div>
          )}
          {/* iter641/iter675 — Honest disclaimer: composite of 18 digital twins */}
          <div
            className={`mt-1 text-[10px] max-w-[180px] text-right leading-tight ${isDark ? "text-gray-500" : "text-gray-500"}`}
            data-testid="cc-hi-score-disclaimer"
          >
            {bg
              ? `Композитна оценка от ${totalTwins || ""} дигитални близнака. Не е диагноза.`
              : `Composite of ${totalTwins || ""} digital twins. Not a diagnosis.`}
          </div>
        </div>
      </div>
    </a>
  );
}
